import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Crown, Loader2 } from "lucide-react";
import { http, fmtErr } from "../lib/api";
import { useConfig } from "../contexts/ConfigContext";
import { ProductImage } from "../components/ProductImage";
import { WaitlistForm } from "../components/WaitlistForm";

export default function Club() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const { purchases_enabled } = useConfig();
  useEffect(() => { http.get("/products").then(({ data }) => setProducts(data.products.filter((p) => !p.coming_soon))); }, []);

  const toggle = (id) => {
    setPicks((prev) => {
      if (prev.includes(id)) return prev.filter((p) => p !== id);
      if (prev.length >= 4) { toast("Pick exactly 4"); return prev; }
      return [...prev, id];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (picks.length !== 4) { toast.error("Please pick exactly 4 candies"); return; }
    setLoading(true);
    try {
      const { data } = await http.post("/subscription/apply", {
        ...form,
        candy_choices: picks,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const input = "w-full bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F]";

  return (
    <div className="bg-[#FFFDF8]" data-testid="club-root">
      <div className="bg-gradient-to-br from-[#1A1A1A] via-[#B71C1C] to-[#D32F2F] text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FFB300] text-[#1A1A1A] px-4 py-2 text-xs tracking-[0.3em] uppercase font-heading">
              <Crown size={14} /> Chamoy Club · $20/mo
            </div>
            <h1 className="font-heading text-6xl md:text-7xl uppercase tracking-wide leading-[0.95] mt-6">Join the<br/>Chamoy Club</h1>
            <p className="text-lg text-white/80 mt-6 leading-relaxed max-w-lg">Get <b className="text-[#FFB300]">4 chilito candies of your choice</b> every month for just <b>$20</b>. Plus early access to new flavors before they hit the shop, exclusive club-only drops, and members-only surprises.</p>
            <ul className="mt-6 space-y-2 text-white/90">
              <li className="flex items-center gap-2"><Check size={18} className="text-[#FFB300]" /> Pick any 4 chilito candies each month</li>
              <li className="flex items-center gap-2"><Check size={18} className="text-[#FFB300]" /> First dibs on new flavor launches</li>
              <li className="flex items-center gap-2"><Check size={18} className="text-[#FFB300]" /> Exclusive club-only mystery items</li>
              <li className="flex items-center gap-2"><Check size={18} className="text-[#FFB300]" /> Cancel anytime</li>
            </ul>
          </div>
          <div className="relative">
            <div className="grid grid-cols-2 gap-3">
              {products.slice(0, 4).map((p, i) => (
                <div key={p.id} className="aspect-square bg-white overflow-hidden relative" style={{ transform: `rotate(${(i % 2 === 0 ? -1 : 1) * 3}deg)` }}>
                  <ProductImage p={p} />
                </div>
              ))}
            </div>
            <div className="absolute -bottom-4 -right-4 bg-[#FFB300] text-[#1A1A1A] p-4 shadow-xl">
              <div className="font-heading text-3xl leading-none">$20</div>
              <div className="text-[10px] tracking-widest uppercase">per month</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Sign up</div>
        <h2 className="font-heading text-5xl uppercase tracking-wide">Build your monthly box</h2>
        <p className="text-[#5C5A56] mt-3">Pick exactly 4 candies you want shipped each month. You can change your picks any time before your renewal.</p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="club-picker">
          {products.map((p) => {
            const selected = picks.includes(p.id);
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => toggle(p.id)}
                className={`text-left border-2 p-3 transition-all ${selected ? "border-[#D32F2F] bg-[#D32F2F]/5 -translate-y-1 shadow-lg" : "border-[#E5D8C1] hover:border-[#1A1A1A]"}`}
                data-testid={`club-pick-${p.id}`}
              >
                <div className={`aspect-square ${selected ? "bg-[#FFB300]/30" : "bg-[#FDF4E3]"} relative overflow-hidden`}>
                  <ProductImage p={p} />
                  {selected && (
                    <div className="absolute inset-0 bg-[#D32F2F]/20 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-[#D32F2F] flex items-center justify-center text-white">
                        <Check size={24} strokeWidth={3} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="font-heading text-sm uppercase tracking-wide mt-2 leading-tight">{p.name}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 text-sm text-[#5C5A56]" data-testid="club-count">
          {picks.length}/4 picked {picks.length === 4 && <span className="text-[#7CB342] font-semibold">✓ ready to join</span>}
        </div>

        {purchases_enabled ? (
        <form onSubmit={submit} className="mt-10 bg-[#FDF4E3] border border-[#E5D8C1] p-8 md:p-10 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <label className="block"><span className="text-xs uppercase tracking-widest">Full name *</span>
              <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={input + " mt-2"} data-testid="club-name" />
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest">Email *</span>
              <input required type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={input + " mt-2"} data-testid="club-email" />
            </label>
          </div>
          <label className="block"><span className="text-xs uppercase tracking-widest">Phone</span>
            <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className={input + " mt-2"} data-testid="club-phone" />
          </label>
          <label className="block"><span className="text-xs uppercase tracking-widest">Shipping address *</span>
            <textarea required rows={3} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className={input + " mt-2 resize-none"} placeholder="Street, city, state, zip" data-testid="club-address" />
          </label>
          <label className="block"><span className="text-xs uppercase tracking-widest">Anything else?</span>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className={input + " mt-2 resize-none"} placeholder="Allergies, preferences, etc." data-testid="club-notes" />
          </label>
          <button
            type="submit"
            disabled={loading || picks.length !== 4}
            className="w-full bg-[#D32F2F] text-white hover:bg-[#B71C1C] py-4 uppercase tracking-widest font-heading text-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-transform hover:-translate-y-1"
            data-testid="club-submit"
          >
            {loading && <Loader2 className="animate-spin" size={18} />}
            {picks.length !== 4 ? `Pick ${4 - picks.length} more candy` : "Join club — $20/month"}
          </button>
          <div className="text-xs text-center text-[#5C5A56]">Secure Stripe checkout. Cancel anytime.</div>
        </form>
        ) : (
          <div className="mt-10" data-testid="club-waitlist-wrapper">
            <div className="bg-[#FFB300] text-[#1A1A1A] text-center p-4 font-heading uppercase tracking-widest text-sm mb-4">🚀 Chamoy Club opens soon — save your spot</div>
            <WaitlistForm
              interest="club"
              headline="Reserve your Chamoy Club membership"
              subhead="We're finalizing packaging and shipping partners. Drop your email — you'll be first to join when the club opens (and you'll get a founding-member perk)."
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function ClubSuccess() {
  const [status, setStatus] = useState("checking");
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await http.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (e) {
        console.warn("club status poll failed", e?.message || e);
      }
      attempts++;
      if (attempts < 6) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);
  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="club-success-root">
      {status === "paid" ? (<>
        <div className="w-20 h-20 mx-auto bg-[#FFB300] flex items-center justify-center mb-6"><Crown className="text-[#1A1A1A]" size={40} /></div>
        <h1 className="font-heading text-5xl uppercase tracking-wide">Welcome to the club!</h1>
        <p className="text-[#5C5A56] mt-3">Your first Chamoy Club box ships within 3 business days. We&apos;ll email you before every renewal so you can swap your 4 picks or pause.</p>
        <Link to="/shop" className="inline-block mt-8 border-2 border-[#1A1A1A] px-8 py-3 uppercase tracking-widest font-heading hover:bg-[#1A1A1A] hover:text-white">Keep shopping</Link>
      </>) : status === "checking" ? (<>
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <h1 className="font-heading text-4xl uppercase tracking-wide">Confirming your subscription…</h1>
      </>) : (<>
        <h1 className="font-heading text-4xl uppercase tracking-wide">Payment {status}</h1>
        <p className="text-[#5C5A56] mt-3">If you were charged and don&apos;t see confirmation, email us.</p>
      </>)}
    </div>
  );
}
